import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useProductStore } from '@/store/productStore';
import { useCartStore } from '@/store/cartStore';
import { useOrderStore } from '@/store/orderStore';
import { useWishlistStore } from '@/store/wishlistStore';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ProductCard } from '@/components/ProductCard';
import {
  RiArrowLeftSLine,
  RiShareForwardLine,
  RiHeart3Line,
  RiHeart3Fill,
  RiSubtractLine,
  RiAddLine,
  RiCheckLine,
  RiShoppingCartLine,
  RiTruckLine,
  RiShieldCheckLine,
  RiStarFill,
} from 'react-icons/ri';
import { IoRestaurantOutline } from 'react-icons/io5';
import type { Product, OrderItem } from '@/utils/supabase';

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, currentLang } = useI18n();
  const { fetchProductById, products, featuredProducts } = useProductStore();
  const addItem = useCartStore((state) => state.addItem);

  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWeight, setSelectedWeight] = useState('250g');
  const [selectedImage, setSelectedImage] = useState(0);
  const [showFullDesc, setShowFullDesc] = useState(false);

  const toggleWishlist = useWishlistStore((state) => state.toggleItem);
  const isWishlisted = useWishlistStore((state) => product ? state.isWishlisted(product.id) : false);

  const descriptionMaxLen = 120;

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) return;
      const productState = useProductStore.getState();
      let cachedProduct = [...productState.products, ...productState.featuredProducts].find(p => p.id === id);
      
      if (!cachedProduct) {
        const orderState = useOrderStore.getState();
        for (const order of orderState.orders) {
          const matchingItem = order.order_items?.find((item: OrderItem) => item.product_id === id);
          if (matchingItem?.products) {
            cachedProduct = matchingItem.products as Product;
            break;
          }
        }
      }

      if (cachedProduct) {
        setProduct(cachedProduct);
        if (cachedProduct.weight_grams) {
          const weightStr = cachedProduct.weight_grams >= 1000 
            ? `${cachedProduct.weight_grams / 1000}kg` 
            : `${cachedProduct.weight_grams}g`;
          setSelectedWeight(weightStr);
        }
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }

      const apiProduct = await fetchProductById(id);
      if (apiProduct) {
        setProduct(apiProduct);
        if (!cachedProduct && apiProduct.weight_grams) {
          const weightStr = apiProduct.weight_grams >= 1000 
            ? `${apiProduct.weight_grams / 1000}kg` 
            : `${apiProduct.weight_grams}g`;
          setSelectedWeight(weightStr);
        }
      }
      setIsLoading(false);
    };

    loadProduct();
  }, [id, fetchProductById]);

  const handleAddToCart = () => {
    if (!product) return;
    const grams = selectedWeight.includes('kg') 
      ? parseFloat(selectedWeight) * 1000 
      : parseInt(selectedWeight);

    addItem({
      productId: product.id,
      quantity,
      price: product.price,
      name: product.name_en,
      nameBn: product.name_bn,
      image: product.images[0] || '',
      weight: grams,
      stock: product.stock_quantity,
    });
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  const handleShare = async () => {
    if (!product) return;
    const shareData = {
      title: currentLang === 'bn' ? product.name_bn : product.name_en,
      text: currentLang === 'bn' 
        ? `${product.name_bn} দেখুন Chocolate Vibes-এ!` 
        : `Check out ${product.name_en} at Chocolate Vibes!`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success(currentLang === 'bn' ? 'লিঙ্ক কপি করা হয়েছে!' : 'Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product) {
      toggleWishlist({
        productId: product.id,
        name: product.name_en,
        nameBn: product.name_bn,
        image: product.images[0] || '',
        price: product.price,
      });
    }
  };

  const incrementQuantity = () => {
    if (product && quantity < product.stock_quantity) setQuantity(q => q + 1);
  };
  const decrementQuantity = () => {
    if (quantity > 1) setQuantity(q => q - 1);
  };

  const allImages = (product?.images?.length || 0) > 0 ? product!.images : ['/placeholder-product.png'];

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-white">
        <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 bg-white">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-5">
          <RiShoppingCartLine className="h-10 w-10 text-gray-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {currentLang === 'bn' ? 'পণ্য পাওয়া যায়নি' : 'Product Not Found'}
        </h2>
        <p className="text-gray-500 text-sm text-center mb-8 max-w-xs">
          {currentLang === 'bn' ? 'পণ্যটি স্টকে নেই বা সরানো হয়েছে।' : 'The product might have been moved or removed from stock.'}
        </p>
        <Button 
          onClick={() => navigate('/products')} 
          className="w-full max-w-xs rounded-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold"
        >
          <RiArrowLeftSLine className="h-5 w-5 mr-1" />
          {t('nav.products')}
        </Button>
      </div>
    );
  }

  const name = currentLang === 'bn' ? product.name_bn : product.name_en;
  const description = currentLang === 'bn' ? product.description_bn : product.description_en;
  const isOutOfStock = product.stock_quantity <= 0;
  const hasDiscount = product.compare_price && product.compare_price > product.price;
  const discountPercent = hasDiscount ? Math.round(((product.compare_price! - product.price) / product.compare_price!) * 100) : 0;

  const truncatedDesc = description && description.length > descriptionMaxLen 
    ? description.slice(0, descriptionMaxLen) + '...' 
    : description;

  const allProductsMap = new Map();
  [...products, ...featuredProducts].forEach(p => allProductsMap.set(p.id, p));
  const uniqueProducts = Array.from(allProductsMap.values());

  const giftBoxes = uniqueProducts.filter(p => p.category === 'gift_boxes' && p.id !== product?.id).slice(0, 10);
  const otherProducts = uniqueProducts.filter(p => p.category !== product?.category && p.id !== product?.id).slice(0, 10);

  return (
    <div className="min-h-[100dvh] bg-white pb-24 animate-fade-in">
      {/* ─── Clean Minimal Header ─── */}
      <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 hover:bg-gray-100 transition-colors active:scale-95"
          >
            <RiArrowLeftSLine className="h-6 w-6 text-gray-800" />
          </button>
          <h1 className="text-sm font-bold text-gray-900 tracking-wide">
            Details
          </h1>
          <button 
            onClick={handleShare}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 hover:bg-gray-100 transition-colors active:scale-95"
            aria-label="Share product"
          >
            <RiShareForwardLine className="h-5 w-5 text-gray-800" />
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto">
        {/* ─── Hero Image Carousel ─── */}
        <div className="relative bg-gray-50 aspect-square mx-4 rounded-[28px] overflow-hidden">
          <img
            src={allImages[selectedImage]}
            alt={name}
            className="w-full h-full object-cover"
            loading="eager"
          />

          {/* Wishlist Heart */}
          <button
            onClick={handleToggleWishlist}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm hover:scale-110 active:scale-95 transition-all z-10"
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            {isWishlisted ? (
              <RiHeart3Fill className="h-5 w-5 text-red-500" />
            ) : (
              <RiHeart3Line className="h-5 w-5 text-gray-500" />
            )}
          </button>
          
          {/* Discount Badge */}
          {hasDiscount && (
            <div className="absolute top-4 left-4 bg-red-500 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg">
              -{discountPercent}%
            </div>
          )}

          {/* Image dots */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {allImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`transition-all rounded-full ${
                    selectedImage === idx 
                      ? 'w-6 h-2 bg-gray-900' 
                      : 'w-2 h-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* ─── Thumbnail Gallery ─── */}
        {allImages.length > 1 && (
          <div className="flex gap-3 px-4 mt-4 overflow-x-auto pb-2 no-scrollbar">
            {allImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all ${
                  selectedImage === idx 
                    ? 'border-gray-900 shadow-md' 
                    : 'border-gray-100 opacity-60'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* ─── Product Info ─── */}
        <div className="px-6 pt-6 space-y-5">
          {/* Name */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h1 className="text-2xl font-black text-gray-900 leading-tight">{name}</h1>
              <p className="text-sm text-gray-500 mt-1 font-medium">
                {t(`categories.${product.category}`)}
              </p>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-black text-gray-900">
              ৳{product.price.toFixed(0)}
            </span>
            {hasDiscount && (
              <span className="text-lg text-gray-400 line-through font-bold">
                ৳{product.compare_price?.toFixed(0)}
              </span>
            )}
          </div>

          {/* Rating mini */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map(i => (
                <RiStarFill key={i} className={`h-4 w-4 ${i <= 4 ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
              ))}
            </div>
            <span className="text-sm font-bold text-gray-700">4.8</span>
            <span className="text-sm text-gray-400">(120+ reviews)</span>
            <span className="ml-auto text-xs font-bold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
              {product.stock_quantity} {currentLang === 'bn' ? 'স্টকে' : 'in stock'}
            </span>
          </div>

          {/* ─── Description ─── */}
          <div className="space-y-2">
            <p className="text-gray-500 text-sm leading-relaxed">
              {showFullDesc ? description : truncatedDesc}
              {description && description.length > descriptionMaxLen && (
                <button
                  onClick={() => setShowFullDesc(!showFullDesc)}
                  className="ml-1 text-amber-600 font-bold text-sm hover:underline"
                >
                  {showFullDesc 
                    ? (currentLang === 'bn' ? ' কম দেখুন' : ' Show less') 
                    : (currentLang === 'bn' ? ' আরও পড়ুন' : ' Read more')
                  }
                </button>
              )}
            </p>
          </div>



          {/* ─── Mini Info Row ─── */}
          <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar -mx-6 px-6 snap-x">
            <div className="min-w-[140px] flex-1 bg-gray-50 rounded-2xl p-3 flex items-center gap-2.5 snap-start">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm shrink-0">
                <RiTruckLine className="h-4 w-4 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">Delivery</p>
                <p className="text-xs font-bold text-gray-900 truncate">1-2 Days</p>
              </div>
            </div>
            <div className="min-w-[140px] flex-1 bg-gray-50 rounded-2xl p-3 flex items-center gap-2.5 snap-start">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm shrink-0">
                <RiShieldCheckLine className="h-4 w-4 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">Quality</p>
                <p className="text-xs font-bold text-gray-900 truncate">Premium</p>
              </div>
            </div>
            <div className="min-w-[140px] flex-1 bg-gray-50 rounded-2xl p-3 flex items-center gap-2.5 snap-start">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm shrink-0">
                <IoRestaurantOutline className="h-4 w-4 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">Taste</p>
                <p className="text-xs font-bold text-gray-900 truncate">Authentic</p>
              </div>
            </div>
          </div>

          {/* ─── Ingredients ─── */}
          {product.ingredients && product.ingredients.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-900">
                {t('product.ingredients')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {product.ingredients.map((ingredient, index) => (
                  <span 
                    key={index} 
                    className="px-3 py-1.5 bg-gray-50 text-gray-600 text-xs font-bold rounded-full border border-gray-100"
                  >
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── Gift Packages Section ─── */}
        {giftBoxes.length > 0 && (
          <div className="mt-8 mb-6">
            <div className="relative flex items-center justify-center py-4 px-6">
              <div className="absolute inset-0 flex items-center px-6">
                <div className="w-full border-t border-dashed border-gray-300"></div>
              </div>
              <h2 className="relative bg-white px-4 text-lg font-black text-gray-900 tracking-wide uppercase text-center">
                {currentLang === 'bn' ? 'উপহার প্যাকেজ' : 'Gift Packages'}
              </h2>
            </div>
            <div className="flex gap-4 overflow-x-auto px-6 pb-4 no-scrollbar snap-x">
              {giftBoxes.map((p) => (
                <div key={p.id} className="w-[160px] sm:w-[200px] flex-shrink-0 snap-start">
                  <ProductCard product={p} loading="lazy" />
                </div>
              ))}
              <div className="w-4 flex-shrink-0" aria-hidden="true" />
            </div>
          </div>
        )}

        {/* ─── People love to add Section ─── */}
        {otherProducts.length > 0 && (
          <div className="mt-4 mb-0">
            <div className="relative flex items-center justify-center py-4 px-6">
              <div className="absolute inset-0 flex items-center px-4">
                <div className="w-full border-t border-dashed border-black"></div>
              </div>
              <h2 className="relative bg-white px-4 text-sm font-semibold text-black tracking-wide text-center">
                {currentLang === 'bn' ? 'মানুষ আরও যা পছন্দ করে' : 'People love to add'}
              </h2>
            </div>
            <div className="flex gap-2 overflow-x-auto pl-2 pb-4 no-scrollbar snap-x">
              {otherProducts.map((p) => (
                <div key={p.id} className="w-[160px] sm:w-[200px] flex-shrink-0 snap-start">
                  <ProductCard product={p} loading="lazy" />
                </div>
              ))}
              <div className="w-4 flex-shrink-0" aria-hidden="true" />
            </div>
          </div>
        )}
      </div>

      {/* ─── Sticky Bottom Action Bar ─── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 px-3 sm:px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="max-w-2xl mx-auto flex items-center gap-2 sm:gap-4">
          {/* Quantity */}
          {!isOutOfStock && (
            <div className="flex items-center bg-gray-50 rounded-full h-12 px-1 border border-primary shrink-0">
              <button
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-primary shadow-sm text-white disabled:opacity-50 active:scale-90 transition-all shrink-0"
              >
                <RiSubtractLine size={16} />
              </button>
              <span className="w-8 sm:w-10 text-center font-black text-xl sm:text-lg text-black">{quantity}</span>
              <button
                onClick={incrementQuantity}
                disabled={quantity >= product.stock_quantity}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-primary shadow-sm text-white disabled:opacity-50 active:scale-90 transition-all shrink-0"
              >
                <RiAddLine size={16} />
              </button>
            </div>
          )}

          {/* Add to Cart CTA */}
          <Button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`h-12 flex-1 rounded-full font-semibold text-sm sm:text-base shadow-lg transition-all px-2 sm:px-4 ${
              isAdded 
                ? 'bg-white hover:bg-white text-primary !border-solid !border !border-primary !shadow-none' 
                : 'bg-primary hover:bg-primary text-white'
            }`}
          >
            {isAdded ? (
              <div className="flex items-center gap-2 sm:gap-2">
                <RiCheckLine size={18} className="shrink-0" />
                <span className="truncate">{currentLang === 'bn' ? 'কার্টে যোগ হয়েছে' : 'Added to Cart'}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 sm:gap-2">
                <RiShoppingCartLine size={18} className="shrink-0" />
                <span className="truncate">{currentLang === 'bn' ? 'কার্টে যোগ করুন' : 'Add to Cart'}</span>
              </div>
            )}
          </Button>

          {/* Buy Now (secondary) */}
          <Button
            variant="outline"
            onClick={handleBuyNow}
            disabled={isOutOfStock}
            className="h-12 rounded-full font-bold text-base px-6 border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all hidden sm:flex"
          >
            {t('btn.buy_now')}
          </Button>
        </div>
      </div>
    </div>
  );
}
